/** The sidebar's navigation: the space's sections (Items with its kinds and entities beneath), and your account. */
import type { ComponentChildren } from "preact";
import type { SpaceOut } from "../api/types.gen.ts";
import { type Key, t } from "../i18n/index.ts";
import { countHref } from "../lib/countLinks.ts";
import { spaceUrl } from "../lib/paths.ts";
import { location } from "../lib/router.ts";
import { reflow } from "../lib/shift.ts";
import { me, usesPills } from "../lib/state.ts";
import { useStored } from "../lib/storage.ts";
import { Avatar } from "../ui/Avatar.tsx";
import { Link } from "../ui/Button.tsx";
import { CountPill } from "../ui/counts.tsx";
import { EntityGlyph } from "../ui/EntityGlyph.tsx";
import { hues, ICON_BG } from "../ui/iconColors.ts";
import { DiscloseIcon, KIND_ICONS, NAV_ICONS } from "../ui/icons.tsx";
import { Badge } from "../ui/misc.tsx";

type Count = "triage" | "queue" | null;
const NAV: [keyof typeof NAV_ICONS, Key, Count][] = [
  ["triage", "nav.triage", "triage"],
  ["queue", "nav.queue", "queue"],
  ["conversation", "nav.conversation", null],
  ["reports", "nav.reports", null],
  ["items", "nav.items", null],
  ["rules", "nav.rules", null],
  ["history", "nav.history", null],
  ["bookmarks", "nav.bookmarks", null],
  ["trash", "nav.trash", null],
  ["settings", "nav.settings", null],
];

/** A sidebar row: its link, and beside it (outside the link) its counts; the row shows hover and current. */
const NAV_ROW = "flex items-center gap-2 rounded-md pr-2 hover:bg-zinc-200/60 has-[>a[aria-current]]:bg-zinc-200";
/** An entity's row (one with an icon): in its tint throughout (a shade deeper on hover, deeper still when current). */
const ENTITY_ROW =
  "flex items-center gap-2 rounded-md pr-2 hover:brightness-[.97] has-[>a[aria-current]]:brightness-[.93]";
const NAV_LINK =
  "min-w-0 flex-1 truncate px-3 py-1.5 text-sm text-zinc-700 aria-[current]:font-medium aria-[current]:text-zinc-900";
/** The room for a row's icon (or its fold's chevron). */
const GLYPH_BOX = "flex size-5 shrink-0 items-center justify-center";

const ENTITY_PAGE_SEGMENTS = 2;
/** The sidebar's counts leave notes out (nothing to do about them): errors, warnings and the triage area. */
const withoutNotes = <T extends { notes: number }>(counts: T): T => ({ ...counts, notes: 0 });
/** Whether the unpinned entities are shown too (remembered in this browser). */
const MORE_ENTITIES = "revisa.nav.moreEntities";

/** The entity whose items, fields or issues are shown, if any. */
function entityHere(): string | null {
  const path = location.value.pathname;
  if (path.endsWith("/items") || path.endsWith("/issues")) {
    return location.value.searchParams.get("entity");
  }
  const [kind, id] = path.split("/").slice(-ENTITY_PAGE_SEGMENTS);
  return kind === "entities" && id && id !== "new" && id !== "map" ? id : null;
}

/** The kind whose items are shown, if any. */
function kindHere(): string | null {
  return location.value.pathname.endsWith("/items") ? location.value.searchParams.get("kind") : null;
}

/** A section's icon, muted until its link is the current one. */
function SectionGlyph({ children }: { children: ComponentChildren }) {
  return <span class={`${GLYPH_BOX} text-zinc-500 in-aria-[current]:text-zinc-900`}>{children}</span>;
}

/**
 * A sidebar link: its icon, then its name. `sub`: one of a section's parts (a kind, an entity), a little tighter;
 * `class`: more of its look.
 */
function NavItem(p: {
  href: string;
  current: "page" | "true" | undefined;
  onNavigate: () => void;
  icon: ComponentChildren;
  label: string;
  sub?: boolean;
  class?: string;
}) {
  return (
    <Link
      href={p.href}
      onClick={p.onNavigate}
      aria-current={p.current}
      class={`${NAV_LINK} flex items-center gap-1.5 ${p.sub ? "py-0.5 pl-2" : "pl-2"}${p.class ? ` ${p.class}` : ""}`}
    >
      {p.icon}
      <span class="truncate">{p.label}</span>
    </Link>
  );
}

/** Under Items, before the entities: one link per kind of item the space has (emails, files…). */
function KindLinks({ space, onNavigate }: { space: SpaceOut; onNavigate: () => void }) {
  const here = kindHere();
  return space.kinds.length === 0 ? null : (
    <ul class="mb-1 flex flex-col border-b border-zinc-200 pb-1">
      {space.kinds.map(({ kind, counts }) => {
        const Glyph = KIND_ICONS[kind];
        return (
          <li key={kind} data-nav={`kind-${kind}`} class={NAV_ROW}>
            <NavItem
              href={`${spaceUrl(space, "/items")}?kind=${kind}`}
              current={here === kind ? "page" : undefined}
              onNavigate={onNavigate}
              sub={true}
              icon={
                <SectionGlyph>
                  <Glyph />
                </SectionGlyph>
              }
              label={t(`items.kindFilter.${kind}`)}
            />
            <CountPill counts={withoutNotes(counts)} triage={counts.triage} href={countHref(space, null, kind)} />
          </li>
        );
      })}
    </ul>
  );
}

/** Under Items: one link per entity (the items of that entity), and the items without one. */
function EntityLinks({ space, onNavigate }: { space: SpaceOut; onNavigate: () => void }) {
  const here = entityHere();
  const [more, setMore] = useStored(MORE_ENTITIES, (raw) => raw === "true");
  // The pinned ones (and the one open now); the rest when asked for.
  const shown = (e: SpaceOut["entities"][number]) => more || e.pinned || e.id === here;
  // Items without an entity are folded away with the unpinned (unless open now); with every entity pinned there's
  // nothing else to fold, so they show instead of the toggle.
  const uncategorized = space.entities.length > 0;
  const allPinned = space.entities.every((e) => e.pinned);
  const uncategorizedShown = uncategorized && (more || allPinned || here === "none");
  const hidden = space.entities.filter((e) => !shown(e)).length + (uncategorized && !uncategorizedShown ? 1 : 0);
  // Pinned first, in their order, then the rest: opening "more" adds below them instead of reshuffling them.
  const ordered = [...space.entities.filter((e) => e.pinned), ...space.entities.filter((e) => !e.pinned)];
  const links = [
    ...ordered.filter(shown).map((e) => ({ id: e.id, label: e.name, icon: e.icon, counts: e.counts })),
    ...(uncategorizedShown ? [{ id: "none", label: t("items.noEntity"), icon: null, counts: space.no_entity }] : []),
  ];
  // Pills: each row in its entity's tint, the icon without its own square; icons: the icons on theirs.
  const pills = usesPills();
  return space.entities.length === 0 ? null : (
    <ul class={`mb-1 flex flex-col ${pills ? "gap-0.5" : ""}`}>
      {links.map((l) => (
        <li
          key={l.id}
          data-nav={`entity-${l.id}`}
          class={pills && l.icon ? `${ENTITY_ROW} ${ICON_BG[hues(l.icon).tint]}` : NAV_ROW}
        >
          <NavItem
            href={`${spaceUrl(space, "/items")}?entity=${l.id}`}
            current={here === l.id ? "page" : undefined}
            onNavigate={onNavigate}
            sub={true}
            {...(l.id === "none" ? { class: "text-zinc-500" } : {})}
            icon={
              <EntityGlyph
                icon={pills && l.icon ? { name: l.icon.name, color: hues(l.icon).color } : l.icon}
                size={20}
                empty={l.id === "none" ? "none" : "dot"}
              />
            }
            label={l.label}
          />
          <CountPill counts={withoutNotes(l.counts)} triage={l.counts.triage} href={countHref(space, l.id)} />
        </li>
      ))}
      {hidden > 0 || (more && !allPinned) ? (
        <li data-nav="more" class={NAV_ROW}>
          <button
            type="button"
            aria-expanded={more}
            // In the iOS app, the sidebar's rows moving to where they are then, those coming or going fading.
            onClick={() => reflow("nav", () => setMore(!more))}
            class={`${NAV_LINK} flex items-center gap-1.5 py-0.5 pl-2 text-left text-zinc-500`}
          >
            <span class={GLYPH_BOX}>
              <DiscloseIcon size={14} class={`transition-transform ${more ? "-rotate-90" : "rotate-90"}`} />
            </span>
            {more ? t("nav.fewerEntities") : t("nav.moreEntities", { count: hidden })}
          </button>
        </li>
      ) : null}
    </ul>
  );
}

/** The space's sections; Items with its kinds and entities beneath it. */
export function Nav({ space, section, onNavigate }: { space: SpaceOut; section: string; onNavigate: () => void }) {
  const entity = entityHere();
  // Items is the page itself when no entity is picked; the entities' and issues' pages are part of its section.
  const current = (path: string) => {
    if (path !== "items") {
      return section === path ? "page" : undefined;
    }
    if (entity || kindHere()) {
      return;
    }
    if (section === "issues") {
      return "true";
    }
    return section === "items" ? "page" : undefined;
  };
  return (
    <nav aria-label={t("nav.label")} class="flex flex-col gap-0.5">
      {NAV.map(([path, label, count]) => {
        const Glyph = NAV_ICONS[path];
        return (
          <div key={path}>
            <div data-nav={path} class={NAV_ROW}>
              <NavItem
                href={spaceUrl(space, `/${path}`)}
                current={current(path)}
                onNavigate={onNavigate}
                icon={
                  <SectionGlyph>
                    <Glyph />
                  </SectionGlyph>
                }
                label={t(label)}
              />
              {path === "items" ? (
                <CountPill counts={withoutNotes(space.counts)} href={countHref(space, null)} />
              ) : null}
              {count !== null && space.counts[count] > 0 ? <Badge>{space.counts[count]}</Badge> : null}
            </div>
            {path === "items" ? (
              // Parts of Items: indented under it, along a guide line.
              <div data-nav="guide" class="my-0.5 ml-2 border-l border-zinc-200/70 pl-0.5">
                <KindLinks space={space} onNavigate={onNavigate} />
                <EntityLinks space={space} onNavigate={onNavigate} />
              </div>
            ) : null}
          </div>
        );
      })}
    </nav>
  );
}

/** Your account, the same in every sidebar (marked while you're on it). */
export function AccountNav({ current, onNavigate }: { current: boolean; onNavigate?: () => void }) {
  return (
    <nav aria-label={t("nav.account")} class="mt-6 flex flex-col gap-0.5 border-t border-zinc-200 pt-3">
      <Link
        href="/profile"
        data-nav="account"
        onClick={onNavigate}
        aria-current={current ? "page" : undefined}
        class="flex items-center gap-2 rounded-md px-3 py-1.5 text-sm text-zinc-700 hover:bg-zinc-200/60 aria-[current=page]:bg-zinc-200 aria-[current=page]:font-medium aria-[current=page]:text-zinc-900"
      >
        {me.value ? <Avatar id={me.value.id} name={me.value.name} kind={me.value.kind} size={20} /> : null}
        {t("account.title")}
      </Link>
    </nav>
  );
}
